import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { TagStatus } from 'common/components/TagStatus/TagStatus'
import { UtilService } from 'common/services/utilService'
import type { TAG_STATUS } from 'common/types/tagStatusType'

import type { IBooking } from 'features/Booking'
import { isUnavailableLease } from 'features/Booking/services/leaseAvailabilityService'

import { PathUtils } from 'routes/routes.utils'

import styles from './completedBookings.module.scss'

interface CompletedBookingsColumnsProps {
  siteId: string
  onUnavailableLease: (booking: IBooking) => void
}

export interface CompletedBookingsColumn {
  title: string
  dataIndex: string
  key: string
  width?: number
  sorter?: (a: IBooking, b: IBooking) => number | string | boolean
  render?: (value: string, item: IBooking) => ReactNode
}

const getLeasePath = (siteId: string, booking: IBooking): string =>
  booking.is_promotion
    ? PathUtils.getPromotionDetails(siteId, booking.id)
    : PathUtils.getBookingDetails(siteId, booking.id)

export const completedBookingsColumns = ({
  siteId,
  onUnavailableLease,
}: CompletedBookingsColumnsProps): CompletedBookingsColumn[] => [
  {
    title: 'Booking number',
    dataIndex: 'id',
    key: 'id',
    width: 130,
    sorter: (a: IBooking, b: IBooking) => a.id && b.id && a.id.localeCompare(b.id),
    render: (id: string, item: IBooking) => {
      // An unsigned package has no reachable page, so it opens a dialog rather than a route.
      if (isUnavailableLease(item)) {
        return (
          <button
            type='button'
            className={styles.parentUnavailable}
            onClick={() => onUnavailableLease(item)}>
            {id}
          </button>
        )
      }

      return (
        <Link className='customLink' to={getLeasePath(siteId, item)}>
          {id}
        </Link>
      )
    },
  },
  {
    title: 'Booked',
    dataIndex: 'lease_type_name',
    key: 'lease_type_name',
    sorter: (a: IBooking, b: IBooking) => {
      const nameA = a.is_promotion ? 'PACKAGE' : a.lease_type_name.toUpperCase()
      const nameB = b.is_promotion ? 'PACKAGE' : b.lease_type_name.toUpperCase()
      return nameA.localeCompare(nameB)
    },
    render: (lease_type_name: string, item: IBooking) => {
      const name = item.is_promotion ? 'Package' : lease_type_name
      return name.toUpperCase()
    },
  },
  {
    title: 'Started',
    dataIndex: 'start_date',
    key: 'start_date',
    sorter: (a: IBooking, b: IBooking) =>
      new Date(a.start_date).valueOf() - new Date(b.start_date).valueOf(),
  },
  {
    title: 'Ended',
    dataIndex: 'end_date',
    key: 'end_date',
    sorter: (a: IBooking, b: IBooking) =>
      new Date(a.end_date).valueOf() - new Date(b.end_date).valueOf(),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    sorter: (a: IBooking, b: IBooking) => {
      const statusA = UtilService.capitalizeFirstLetter(a.status)
      const statusB = UtilService.capitalizeFirstLetter(b.status)
      return statusA.localeCompare(statusB)
    },
    render: (status: string) => (
      <TagStatus status={UtilService.capitalizeFirstLetter(status) as TAG_STATUS} />
    ),
  },
]
